/**
 * The media library, the site redirects and the publication schedule
 * (document/61: M8, E3, E1). Shapes from `packages/contracts/src/library.ts`,
 * paths and query parameters from `apps/gateway/src/routes/media.ts`,
 * `redirects.ts` and `schedule.ts`. Nothing here is invented: when the
 * gateway does not offer something, the screen does without it.
 */
import type {
  MediaAssetDetail,
  MediaAssetSummary,
  MediaUploadResult,
  Page,
  PublicationSchedule,
  PutRedirectsRequest,
  RedirectList,
  RemoveRedirectsRequest,
  UpdateMediaRequest,
  WriteResult,
} from '@cms/contracts'
import { apiBaseUrl, apiFetch, apiUpload } from './client.js'
import type { WriteEnvelope } from './endpoints.js'
import type { MediaKind, MediaStatusFilter } from '../domain/media.js'

// ---------------------------------------------------------------------------
// Media library
// ---------------------------------------------------------------------------

export interface MediaListQuery {
  q?: string
  kind?: MediaKind
  /** The API's default is `active`. */
  status?: MediaStatusFilter
  limit?: number
  cursor?: string
}

export function listMedia(siteId: string, query: MediaListQuery = {}): Promise<Page<MediaAssetSummary>> {
  return apiFetch(`/sites/${encodeURIComponent(siteId)}/media`, { query: query as Record<string, string | number | undefined> })
}

export function getMedia(id: string): Promise<MediaAssetDetail> {
  return apiFetch(`/media/${encodeURIComponent(id)}`)
}

/**
 * The bytes, behind the session cookie. An `<img>` on the interface's origin
 * sends it (same site, `SameSite=Lax`), and the answer is cached for a year:
 * a new file is a new asset with a new id.
 */
export function mediaFileUrl(id: string): string {
  return `${apiBaseUrl()}/media/${encodeURIComponent(id)}/file`
}

export interface UploadMediaInput {
  file: File
  reason: string
  /** Under `media/`; the gateway generates `media/<yyyy>/<slug>.<ext>` when absent. */
  path?: string
  name?: string
  alt?: string
  altLocale?: string
}

export function uploadMedia(
  siteId: string,
  input: UploadMediaInput,
  idempotencyKey: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<MediaUploadResult> {
  const form = new FormData()
  form.set('file', input.file, input.file.name)
  form.set('reason', input.reason)
  if (input.path) form.set('path', input.path)
  if (input.name) form.set('name', input.name)
  if (input.alt) {
    form.set('alt', input.alt)
    if (input.altLocale) form.set('altLocale', input.altLocale)
  }
  return apiUpload(`/sites/${encodeURIComponent(siteId)}/media`, form, { idempotencyKey, onProgress })
}

export function updateMedia(id: string, body: UpdateMediaRequest, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/media/${encodeURIComponent(id)}`, { method: 'PATCH', body, idempotencyKey })
}

/** Out of the library and of the next publication; the bytes stay. */
export function archiveMedia(id: string, body: WriteEnvelope, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/media/${encodeURIComponent(id)}`, { method: 'DELETE', body, idempotencyKey })
}

export function restoreMedia(id: string, body: WriteEnvelope, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/media/${encodeURIComponent(id)}/restore`, { method: 'POST', body, idempotencyKey })
}

// ---------------------------------------------------------------------------
// Redirects
// ---------------------------------------------------------------------------

export function listRedirects(siteId: string, query: { search?: string; limit?: number; cursor?: string } = {}): Promise<RedirectList> {
  return apiFetch(`/sites/${encodeURIComponent(siteId)}/redirects`, { query })
}

export function putRedirects(siteId: string, body: PutRedirectsRequest, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/sites/${encodeURIComponent(siteId)}/redirects`, { method: 'PUT', body, idempotencyKey })
}

export function removeRedirects(siteId: string, body: RemoveRedirectsRequest, idempotencyKey: string): Promise<WriteResult> {
  return apiFetch(`/sites/${encodeURIComponent(siteId)}/redirects`, { method: 'DELETE', body, idempotencyKey })
}

// ---------------------------------------------------------------------------
// Publication schedule
// ---------------------------------------------------------------------------

/** Earliest first; the gateway returns at most 100 and says how many there are in `total`. */
export function getSchedule(siteId: string, limit = 100): Promise<PublicationSchedule> {
  return apiFetch(`/sites/${encodeURIComponent(siteId)}/schedule`, { query: { limit } })
}
